import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '@/lib/adminApi';
import { Spinner, EmptyState } from '@/components/ui';
import { Drawer, Field } from '@/components/admin/Drawer';
import { WalletPanel } from '@/components/admin/WalletPanel';
import { BlockBanControl } from '@/components/admin/BlockBanControl';
import { ForceLogoutControl } from '@/components/admin/ForceLogoutControl';
import { EditIdentityControl } from '@/components/admin/EditIdentityControl';
import { AccountStatusBadge } from '@/components/admin/shared';
import { personName, dateTime } from '@/lib/adminFormat';

/** CustomerDrawer — full detail for one customer (identity + km-credit wallet
 *  with a manual-adjustment form). Covers the Filament CustomerResource view +
 *  CustomerWalletHistoryResource. */
export function CustomerDrawer({ id, onClose }: { id: number; onClose: () => void }) {
  const [tab, setTab] = useState<'detail' | 'wallet'>('detail');
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'customer', id],
    queryFn: ({ signal }) => adminApi.customer(id, signal),
  });

  const c = data?.customer;

  return (
    <Drawer
      title={c ? personName(c) : `Customer #${id}`}
      subtitle={c ? `${c.country_code}${c.phone}` : undefined}
      onClose={onClose}
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner label="Loading customer…" />
        </div>
      ) : isError ? (
        <EmptyState title="Couldn't load customer" description={(error as Error)?.message} />
      ) : c ? (
        <div className="space-y-4">
          <Tabs tab={tab} onTab={setTab} />

          {tab === 'detail' ? (
            <div className="space-y-4">
              <div>
                <Field label="ID" value={`#${c.id}`} />
                <Field label="Name" value={personName(c)} />
                <Field label="Phone" value={`${c.country_code}${c.phone}`} />
                <Field label="Email" value={c.email || '—'} />
                <Field label="Status" value={<AccountStatusBadge status={c.status} />} />
                <Field label="Stripe ID" value={c.stripe_id || '—'} />
                <Field label="km-credit" value={`${(data?.wallet?.balance_km ?? 0).toFixed(2)} km`} />
                <Field label="Joined" value={dateTime(c.created_at)} />
              </div>
              {/* Edit identity: first/last/email (gated by customers.manage;
                  hidden for roles without it). Phone is read-only. */}
              <EditIdentityControl
                id={c.id}
                firstName={c.first_name}
                lastName={c.last_name}
                email={c.email}
                phone={`${c.country_code}${c.phone}`}
              />
              {/* Trust & safety: block / reinstate (gated by customers.block;
                  hidden for roles without it). */}
              <BlockBanControl scope="customer" id={c.id} status={c.status} />
              {/* Force logout: revoke every session without changing status
                  (same customers.block gate; hidden for roles without it). */}
              <ForceLogoutControl scope="customer" id={c.id} />
            </div>
          ) : (
            <WalletPanel scope="customer" userId={c.id} />
          )}
        </div>
      ) : (
        <EmptyState title="Customer not found" />
      )}
    </Drawer>
  );
}

function Tabs({ tab, onTab }: { tab: 'detail' | 'wallet'; onTab: (t: 'detail' | 'wallet') => void }) {
  const item = (key: 'detail' | 'wallet', label: string) => (
    <button
      type="button"
      onClick={() => onTab(key)}
      className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
        tab === key ? 'bg-brand-600/15 text-brand-400' : 'text-muted hover:bg-ink-800 hover:text-slate-200'
      }`}
    >
      {label}
    </button>
  );
  return (
    <div className="flex gap-1 rounded-lg border border-ink-800 bg-ink-850 p-1">
      {item('detail', 'Detail')}
      {item('wallet', 'Wallet')}
    </div>
  );
}
